import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

type DocumentWithSelection = Document & { selection: Selection };

function unFocus(document: DocumentWithSelection, window: Window) {
  if (document.selection) {
    document.selection.empty();
  } else {
    try {
      window?.getSelection()?.removeAllRanges();
    } catch (e) {
      throw new Error(e);
    }
  }
}

type TMixedEvent = MouseEvent & { touches: Array<{ clientX: number; clientY: number }> };

interface IArgs {
  // OPTIONS
  split?: 'vertical';
  isPrimaryFirst?: boolean;
  allowResize?: boolean;
  isCollapsible?: boolean;

  // SIZES
  minSize?: number;
  maxSize?: number;
  defaultSize?: number;
  size?: number;

  // ACTIONS
  onRender?: (sizes: { pane1Size?: number; pane2Size?: number }) => void;
  onDragStarted?: () => void;
  onChange?: (newSize?: number) => void;
  onDragFinished?: (draggedSize?: number) => void;
}

export default class BvSplitPane extends Component<IArgs> {
  @tracked containerElement?: Element;

  /**
   * Is the pane rendered yet ?
   */
  @tracked isRendered = false;

  @tracked active = false;

  /**
   * Split position
   */
  @tracked position?: number;

  /**
   * First pane DOM element
   */
  @tracked pane1?: Element;

  /**
   * Current size of the first pane
   */
  @tracked pane1CurrentSize?: number;

  /**
   * Second pane DOM element
   */
  @tracked pane2?: Element;

  /**
   * Current size of the second pane
   */
  @tracked pane2CurrentSize?: number;

  /**
   * Is the split being resized
   */
  @tracked resized = false;

  @tracked isCollapsed = false;

  @tracked draggedSize?: number;

  pane1CurrentSizeLastExpanded?: number;

  // Computed Properties
  // ~~~~~

  get split() {
    return this.args.split ?? 'vertical';
  }

  get isPrimaryFirst() {
    return this.args.isPrimaryFirst ?? true;
  }

  get allowResize() {
    return this.args.allowResize ?? true;
  }

  get minSize() {
    return this.args.minSize ?? 50;
  }

  get isCollapsible() {
    return this.args.isCollapsible ?? true;
  }

  /**
   * Is the slit vertical ?
   * @type {boolean}
   */
  get isVertical() {
    return this.split === 'vertical';
  }

  /**
   * Size of the first pane
   * @type {number}
   */
  get pane1Size() {
    const { size, defaultSize } = this.args;
    return this.isPrimaryFirst ? size ?? defaultSize ?? this.minSize : undefined;
  }

  /**
   * Size of the second pane
   * @type {number}
   */
  get pane2Size() {
    const { size, defaultSize } = this.args;
    return !this.isPrimaryFirst ? size ?? defaultSize ?? this.minSize : undefined;
  }

  // Actions

  @action
  handleDidInsert(element: Element) {
    this.containerElement = element;
    this.initSize();
    if (!this.isRendered) {
      this.args.onRender?.({ pane1Size: this.pane1Size, pane2Size: this.pane2Size });
    }
    this.isRendered = true;
  }

  @action
  handleSizeUpdate(/* el: Element, [size]: [number] */) {
    this.initSize();
  }

  @action
  registerPane(isFirst: boolean, paneElement: Element) {
    if (paneElement) {
      this[isFirst ? 'pane1' : 'pane2'] = paneElement;
    }
  }

  @action
  unregisterPane(isFirst: boolean) {
    const key = isFirst ? 'pane1' : 'pane2';
    if (this[key]) {
      this[key] = undefined;
    }
  }

  // Helpers

  initSize() {
    next(this, () => {
      this.setSize();
    });
  }

  @action
  handleMouseDown(event: MouseEvent) {
    const eventWithTouches = Object.assign({}, event, {
      touches: [
        {
          clientX: event.clientX,
          clientY: event.clientY
        }
      ]
    });
    this.handleTouchStart(eventWithTouches);
  }

  @action
  handleTouchStart(event: TMixedEvent) {
    if (this.allowResize) {
      unFocus(document as DocumentWithSelection, window);
      const position =
        this.split === 'vertical' ? event.touches[0].clientX : event.touches[0].clientY;

      this.args.onDragStarted?.();

      this.active = true;
      this.position = position;
    }
  }

  @action
  handleMouseMove(event: MouseEvent) {
    const eventWithTouches = Object.assign({}, event, {
      touches: [
        {
          clientX: event.clientX,
          clientY: event.clientY
        }
      ]
    });
    this.handleTouchMove(eventWithTouches);
  }

  @action
  handleTouchMove(event: TMixedEvent) {
    const { maxSize, onChange } = this.args;
    const { active, position, allowResize, isPrimaryFirst, minSize, split } = this;

    if (allowResize && active) {
      unFocus(document as DocumentWithSelection, window);
      const refKey = isPrimaryFirst ? 'pane1' : 'pane2';
      const ref = this[refKey];
      if (ref) {
        if (ref.getBoundingClientRect) {
          const { width, height } = ref.getBoundingClientRect();
          const current =
            split === 'vertical' ? event.touches[0].clientX : event.touches[0].clientY;
          const size = split === 'vertical' ? width : height;
          const newPosition = isPrimaryFirst
            ? (position ?? 0) - current
            : current - (position ?? 0);

          let newMaxSize = maxSize ?? 0;

          if (isPresent(maxSize) && maxSize! <= 0) {
            const parentRect = this.containerElement?.getBoundingClientRect();
            if (parentRect) {
              newMaxSize = (split === 'vertical' ? parentRect.width : parentRect.height) + maxSize!;
            }
          }

          let newSize = size - newPosition;
          if (newSize < minSize && !this.isCollapsible) {
            newSize = minSize;
          } else if (isPresent(maxSize) && newSize > newMaxSize!) {
            newSize = newMaxSize;
          } else {
            this.position = current;
            this.resized = true;
          }

          onChange?.(newSize);

          this.draggedSize = newSize;
          this[`${refKey}CurrentSize` as const] = newSize;
        }
      }
    }
  }

  @action
  handleMouseUp() {
    if (this.allowResize && this.active) {
      if ((this.draggedSize ?? 0) < this.minSize - 50 && this.isCollapsible && !this.isCollapsed) {
        this.toggleCollapse(true);
      } else {
        this.pane1CurrentSizeLastExpanded = this.draggedSize;
        this.toggleCollapse(false);
      }
      this.args.onDragFinished?.(this.draggedSize);
      this.active = false;
    }
  }

  @action
  handleToggleClick(/* event */) {
    this.toggleCollapse(!this.isCollapsed);
  }

  // Helpers

  toggleCollapse(collapse?: boolean) {
    this.isCollapsed = Boolean(collapse);
    if (this.isCollapsed) {
      this.pane1CurrentSizeLastExpanded = Math.max(this.pane1CurrentSize || 0, this.minSize);
      this.pane1CurrentSize = 0;
    } else {
      this.pane1CurrentSize = Math.max(
        this.pane1CurrentSizeLastExpanded ?? 0,
        this.draggedSize ?? 0
      );
    }
  }
  setSize() {
    const refKey = this.isPrimaryFirst ? 'pane1' : 'pane2';

    if (this[refKey]) {
      const newSize = this.args.size || this.draggedSize || this.args.defaultSize || this.minSize;
      this[`${refKey}CurrentSize` as const] = newSize;

      if (this.args.size !== this.draggedSize) {
        this.draggedSize = newSize;
      }
    }
  }
}
